import * as React from "react"

export const MIN_COLUMN_WIDTH = 60

/**
 * Pointer-drag column resizing.
 *
 * `startResize(key, event)` is meant to be wired to a drag handle's
 * onPointerDown (SortableTh does this via its `onResize` prop). Listeners are
 * attached to window for the duration of the drag and removed on pointerup or
 * unmount. Widths never shrink below MIN_COLUMN_WIDTH.
 */
export function useColumnResize(defaults: Record<string, number>) {
  const [widths, setWidths] = React.useState<Record<string, number>>(defaults)

  const widthsRef = React.useRef(widths)
  widthsRef.current = widths

  const defaultsRef = React.useRef(defaults)
  defaultsRef.current = defaults

  const cleanupRef = React.useRef<(() => void) | null>(null)
  React.useEffect(
    () => () => {
      cleanupRef.current?.()
    },
    []
  )

  const startResize = React.useCallback(
    (key: string, event: React.PointerEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      const startX = event.clientX
      const startWidth =
        widthsRef.current[key] ?? defaultsRef.current[key] ?? MIN_COLUMN_WIDTH
      const previousCursor = document.body.style.cursor
      document.body.style.cursor = "col-resize"

      const onPointerMove = (moveEvent: PointerEvent) => {
        const nextWidth = Math.max(
          MIN_COLUMN_WIDTH,
          startWidth + moveEvent.clientX - startX
        )
        setWidths((current) => ({ ...current, [key]: nextWidth }))
      }
      const cleanup = () => {
        document.body.style.cursor = previousCursor
        window.removeEventListener("pointermove", onPointerMove)
        window.removeEventListener("pointerup", cleanup)
        cleanupRef.current = null
      }
      window.addEventListener("pointermove", onPointerMove)
      window.addEventListener("pointerup", cleanup)
      cleanupRef.current = cleanup
    },
    []
  )

  return { widths, startResize }
}
